import { PencilIcon, PlusSmIcon } from "@heroicons/react/outline";
import { useMemo, useState } from "react";
import { ICellRendererParams } from "ag-grid-community";

import { ProviderWithLogo } from "../../ProviderWithLogo";

import { useAuth } from "@/context/auth-context";
import { HUGGINGFACE_PROVIDER } from "@/components/ModelParameters/constants";
import { useProviders } from "@/queries/providers/useProviders";
import { useDeleteProvider } from "@/queries/providers/useDeleteProvider";
import { CustomProvider } from "@/queries/providers/types";
import { ContextMenuItems } from "@/components/ui/ContextMenu/types";
import { Action } from "@/components/ui/DropdownMenu";
import { TableColumn } from "@/components/ui/TableV2";
import { useCustomModels } from "@/queries/models/useCustomModels";
import { CustomModel } from "@/components/CustomModels/types";
import { WrappedCustomModelsCreation } from "@/components/CustomModels";
import { OverflowDisplayTooltip } from "@/components/ui/Tooltip/OverflowDisplayTooltip";

export const useCustomProvidersTable = () => {
  const authContext = useAuth();
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isPreviewModalOpen, setIsPreviewModalOpen] = useState(false);
  const [customProvider, setCustomProvider] = useState<CustomProvider | null>(null);
  const [isModelDialogOpen, setIsModelDialogOpen] = useState(false);
  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false);

  const { mutate: deleteProvider } = useDeleteProvider();
  const { data: models } = useCustomModels({
    perPage: 250,
    page: 1,
  });

  const detailCellRenderer = useMemo(
    () => (params: ICellRendererParams<CustomProvider>) => {
      const { data } = params;
      if (!data) {
        return null;
      }

      return (
        <div className="h-[400px] w-full p-4">
          <WrappedCustomModelsCreation models={data.models} providerName={data.name} />
        </div>
      );
    },
    [],
  );

  const { data: providers, isLoading: isLoadingProviders } = useProviders({
    userToken: authContext?.userToken ?? "",
  });

  const providersWithModels = useMemo(
    () =>
      providers?.map((provider: CustomProvider) => ({
        ...provider,
        models:
          models?.model_configs?.filter(
            (model: CustomModel) => model.provider_id === provider.id,
          ) ?? [],
      })),
    [models, providers],
  );

  const columns: TableColumn<(typeof providers)[number]>[] = [
    {
      id: 1,
      name: "name",
      header: "Name",
      field: "name",
      sortable: false,
      cellRenderer: "agGroupCellRenderer",
      cellRendererParams: {
        innerRenderer: (params: ICellRendererParams<CustomProvider>) => {
          if (!params.data) {
            return null;
          }
          return (
            <div className="ml-2 flex items-center gap-2">
              <ProviderWithLogo provider={params.data.name} />
            </div>
          );
        },
      },
    },
    {
      id: 2,
      name: "type",
      header: "Type",
      field: "type",
      cellRenderer: (params: ICellRendererParams<CustomProvider>) => {
        if (!params.data) {
          return null;
        }
        return params.data.workspace_id ? "Custom" : "Default";
      },
    },
    {
      id: 3,
      name: "client",
      header: "Client",
      field: "client",
      cellRenderer: (params: ICellRendererParams<CustomProvider>) => {
        if (!params.data) {
          return null;
        }

        const isCustomProvider = !!params.data.workspace_id;

        if (!isCustomProvider) {
          return "-";
        }

        return <ProviderWithLogo provider={params.data.client} />;
      },
    },
    {
      id: 4,
      name: "base_url",
      header: "Base URL",
      field: "base_url",
      cellRenderer: (params: ICellRendererParams<CustomProvider>) => {
        if (!params.data) {
          return null;
        }
        return (
          <OverflowDisplayTooltip
            content={params.data.base_url}
            shouldShow={Boolean(params.data.base_url)}
            className="text-sm"
          >
            {params.data.base_url || "-"}
          </OverflowDisplayTooltip>
        );
      },
    },
    {
      id: 5,
      name: "api_key_suffix",
      header: "Key",
      field: "api_key_suffix",
      sortable: false,
      cellRenderer: (params: ICellRendererParams<CustomProvider>) => {
        const suffix = params.data?.api_key_suffix;
        if (!suffix) {
          return "-";
        }
        return <span className="font-mono text-sm">••••••••••••••••••••{suffix}</span>;
      },
    },
    {
      id: 6,
      name: "models",
      header: "# of Custom Models",
      field: "models",
      cellRenderer: (params: ICellRendererParams<CustomProvider>) => {
        if (!params.data) {
          return null;
        }
        return params.data.models.length;
      },
    },
  ];

  const actions: Action<(typeof providers)[number]>[] = [
    // {
    //   id: "view",
    //   label: "View",
    //   icon: EyeIcon,
    //   onClick: (provider) => {
    //     setCustomProvider(provider);
    //     setIsPreviewModalOpen(true);
    //   },
    // },
    {
      id: "add-model",
      label: "Add model",
      icon: PlusSmIcon,
      onClick: provider => {
        setCustomProvider(provider);
        setIsModelDialogOpen(true);
      },
    },
    {
      id: "edit",
      label: "Edit",
      icon: PencilIcon,
      onClick: provider => {
        setCustomProvider(provider);
        setIsEditModalOpen(true);
      },
      getDisabled: provider =>
        provider.provider_name === HUGGINGFACE_PROVIDER || !provider.workspace_id,
    },
  ];

  const contextMenuItems: ContextMenuItems<(typeof providers)[number]>[] = [
    // {
    //   id: "view",
    //   label: "View provider",
    //   icon: EyeIcon,
    //   onClick: (provider) => {
    //     setCustomProvider(provider);
    //     setIsPreviewModalOpen(true);
    //   },
    // },
    {
      id: "add-model",
      label: "Add model",
      icon: PlusSmIcon,
      onClick: provider => {
        setCustomProvider(provider);
        setIsModelDialogOpen(true);
      },
    },
    {
      id: "edit",
      label: "Edit provider",
      icon: PencilIcon,
      onClick: provider => {
        setCustomProvider(provider);
        setIsEditModalOpen(true);
      },
      getDisabled: provider =>
        provider.provider_name === HUGGINGFACE_PROVIDER || !provider.workspace_id,
    },
  ];

  const handleDelete = (provider: CustomProvider) => {
    deleteProvider(provider.id.toString());
  };

  return {
    columns,
    actions,
    contextMenuItems,
    providers,
    isLoadingProviders,
    customProviders: providersWithModels,
    isEditModalOpen,
    setIsEditModalOpen,
    isPreviewModalOpen,
    setIsPreviewModalOpen,
    detailCellRenderer,
    customProvider,
    setCustomProvider,
    isModelDialogOpen,
    setIsModelDialogOpen,
    isConfirmationModalOpen,
    setIsConfirmationModalOpen,
    handleDelete,
  };
};
